import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CloudUpload, Download, FileText, Folder, Loader2, MessageSquare } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import BrandLogo from '../../components/BrandLogo';
import InlinePreview from '../files/components/InlinePreview';
import { formatBytes } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import {
  downloadInvitationFile,
  downloadInvitationFolderFile,
  fetchInvitationPreviewBlob,
  resolveInvitationMeta,
} from './invitationApi';

/**
 * Landing page for an emailed "you've been shared a document" link — public,
 * no login required to preview (identity is the security boundary, not a
 * shared secret). Chat & comments are never shown here even if the grant
 * allows them; that only unlocks post-login at /shared/:fileId.
 */
export default function InvitationPreviewPage() {
  const { token } = useParams();

  const meta = useQuery({
    queryKey: ['invitation-meta', token],
    queryFn: () => resolveInvitationMeta(token),
    retry: false,
  });

  const isFolder = meta.data?.resourceType === 'folder';

  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => fetchInvitationPreviewBlob(token),
    enabled: meta.isSuccess && !isFolder,
  });

  const canDownload = meta.data?.accessMode !== 'view';
  const handleDownload = async () => {
    const id = toast.loading(`Downloading ${meta.data.name}…`);
    try {
      await downloadInvitationFile(token, meta.data.name);
      toast.success('Download ready', { id });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id });
    }
  };

  const handleFolderFileDownload = async (file) => {
    const id = toast.loading(`Downloading ${file.name}…`);
    try {
      await downloadInvitationFolderFile(token, file.id, file.name);
      toast.success('Download ready', { id });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id });
    }
  };

  const unlockPath = meta.data
    ? isFolder
      ? `/shared-folder/${meta.data.resourceId}`
      : `/shared/${meta.data.resourceId}`
    : null;

  return (
    <div className="flex min-h-full items-start justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex justify-center"><BrandLogo className="h-10 w-auto max-w-full" /></div>

        <div className="rounded-2xl bg-slate-100 p-6 shadow-lg ring-1 ring-slate-100">
          {meta.isLoading ? (
            <Spinner />
          ) : meta.isError ? (
            <p className="py-8 text-center text-sm text-red-600">
              {getErrorMessage(meta.error, 'This invitation is invalid or has been revoked.')}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  {isFolder && <Folder className="h-5 w-5 shrink-0 text-amber-500" />}
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{meta.data.name}</p>
                    <p className="text-sm text-slate-500">
                      You have{' '}
                      {meta.data.accessMode === 'edit'
                        ? 'edit'
                        : meta.data.accessMode === 'download'
                          ? 'view & download'
                          : 'view'}{' '}
                      access
                    </p>
                  </div>
                </div>
                {!isFolder && canDownload && (
                  <Button onClick={handleDownload}>
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                )}
              </div>

              {isFolder ? (
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {meta.data.folders.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600">
                      <Folder className="h-4 w-4 text-amber-500" />
                      {f.name}
                    </div>
                  ))}
                  {meta.data.files.map((file) => (
                    <div key={file.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="flex-1 truncate text-slate-700">{file.name}</span>
                      <span className="text-xs text-slate-400">{formatBytes(file.sizeBytes)}</span>
                      {canDownload && (
                        <button
                          className="rounded p-1 text-slate-400 hover:text-brand-600"
                          onClick={() => handleFolderFileDownload(file)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {meta.data.folders.length === 0 && meta.data.files.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-slate-400">This folder is empty.</div>
                  )}
                </div>
              ) : (
                <div className="h-[60vh] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  {preview.isLoading ? (
                    <div className="flex h-full items-center justify-center text-slate-400">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : preview.isError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
                      <FileText className="h-10 w-10" />
                      <p className="text-sm">Couldn’t load a preview.</p>
                    </div>
                  ) : (
                    <InlinePreview
                      mimeType={meta.data.mimeType}
                      name={meta.data.name}
                      blob={preview.data}
                      allowCopy={canDownload}
                    />
                  )}
                </div>
              )}

              {meta.data.allowChat && (
                <div className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-slate-700">
                  <MessageSquare className="h-4 w-4 shrink-0 text-brand-600" />
                  <span className="flex-1">
                    You&apos;ve also been given access to chat &amp; comments on this document.
                  </span>
                  <Link
                    to="/login"
                    state={{ from: { pathname: unlockPath } }}
                    className="font-medium text-brand-600 hover:text-brand-700"
                  >
                    Log in to unlock
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
